import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import { maintenanceApi, roomsApi, usersApi } from '../../lib/api';
import { format } from 'date-fns';
import {
  Wrench,
  Plus,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Building,
  BedDouble,
  Filter,
  RefreshCw,
  MessageSquare,
  MoreVertical,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

export default function MaintenancePage() {
  const { currentProperty } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  
  // Dialogs
  const [showNewOrderDialog, setShowNewOrderDialog] = useState(false);
  const [showOrderDetailDialog, setShowOrderDetailDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);

  // Form states
  const [orderForm, setOrderForm] = useState({
    room_id: '',
    location_type: 'ROOM',
    location_description: '',
    category_id: '',
    issue_type: 'REPAIR',
    priority: 'NORMAL',
    title: '',
    description: '',
    room_out_of_order: false,
  });
  const [categoryForm, setCategoryForm] = useState({
    code: '',
    name: '',
    description: '',
  });
  const [comment, setComment] = useState('');
  const [updateForm, setUpdateForm] = useState({
    status: '',
    assigned_to: '',
    resolution_notes: '',
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [dashboardRes, ordersRes, categoriesRes, roomsRes, usersRes] = await Promise.all([
          maintenanceApi.getDashboard(currentProperty.id),
          maintenanceApi.listOrders(currentProperty.id),
          maintenanceApi.listCategories(currentProperty.id),
          roomsApi.list(currentProperty.id),
          usersApi.list(),
        ]);
        setDashboard(dashboardRes.data);
        setOrders(ordersRes.data || []);
        setCategories(categoriesRes.data || []);
        setRooms(roomsRes.data || []);
        setUsers(usersRes.data || []);
      } catch (error) {
        console.error('Failed to load maintenance data:', error);
        toast.error('Failed to load maintenance data');
      } finally {
        setLoading(false);
      }
    };

    if (currentProperty?.id) {
      loadInitialData();
    }
  }, [currentProperty]);

  useEffect(() => {
    const loadFilteredOrders = async () => {
      try {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        if (priorityFilter) params.priority = priorityFilter;
        
        const ordersRes = await maintenanceApi.listOrders(currentProperty.id, params);
        setOrders(ordersRes.data || []);
      } catch (error) {
        console.error('Failed to load orders:', error);
      }
    };

    if (currentProperty?.id) {
      loadFilteredOrders();
    }
  }, [currentProperty?.id, statusFilter, priorityFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, ordersRes, categoriesRes, roomsRes, usersRes] = await Promise.all([
        maintenanceApi.getDashboard(currentProperty.id),
        maintenanceApi.listOrders(currentProperty.id),
        maintenanceApi.listCategories(currentProperty.id),
        roomsApi.list(currentProperty.id),
        usersApi.list(),
      ]);
      setDashboard(dashboardRes.data);
      setOrders(ordersRes.data || []);
      setCategories(categoriesRes.data || []);
      setRooms(roomsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to load maintenance data:', error);
      toast.error('Failed to load maintenance data');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      
      const ordersRes = await maintenanceApi.listOrders(currentProperty.id, params);
      setOrders(ordersRes.data || []);
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const handleCreateOrder = async () => {
    try {
      await maintenanceApi.createOrder(currentProperty.id, {
        ...orderForm,
        room_id: orderForm.room_id || null,
        category_id: orderForm.category_id || null,
      });
      
      await loadData();
      setShowNewOrderDialog(false);
      setOrderForm({
        room_id: '',
        location_type: 'ROOM',
        location_description: '',
        category_id: '',
        issue_type: 'REPAIR',
        priority: 'NORMAL',
        title: '',
        description: '',
        room_out_of_order: false,
      });
      toast.success('Work order created');
    } catch (error) {
      toast.error('Failed to create work order');
    }
  };

  const handleUpdateOrder = async () => {
    try {
      if (!selectedOrder) return;
      
      const updateData = {};
      if (updateForm.status) updateData.status = updateForm.status;
      if (updateForm.assigned_to) updateData.assigned_to = updateForm.assigned_to;
      if (updateForm.resolution_notes) updateData.resolution_notes = updateForm.resolution_notes;
      
      await maintenanceApi.updateOrder(selectedOrder.id, updateData);
      
      await loadData();
      setShowOrderDetailDialog(false);
      setSelectedOrder(null);
      toast.success('Work order updated');
    } catch (error) {
      toast.error('Failed to update work order');
    }
  };

  const handleAddComment = async () => {
    try {
      if (!selectedOrder || !comment.trim()) return;
      
      await maintenanceApi.addComment(selectedOrder.id, comment);
      
      // Reload order details
      const orderRes = await maintenanceApi.getOrder(selectedOrder.id);
      setSelectedOrder(orderRes.data);
      
      setComment('');
      setShowCommentDialog(false);
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleCreateCategory = async () => {
    try {
      await maintenanceApi.createCategory(currentProperty.id, categoryForm);
      
      const categoriesRes = await maintenanceApi.listCategories(currentProperty.id);
      setCategories(categoriesRes.data || []);
      
      setShowCategoryDialog(false);
      setCategoryForm({ code: '', name: '', description: '' });
      toast.success('Category created');
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const viewOrderDetails = async (order) => {
    try {
      const orderRes = await maintenanceApi.getOrder(order.id);
      setSelectedOrder(orderRes.data);
      setUpdateForm({
        status: orderRes.data.status,
        assigned_to: orderRes.data.assigned_to || '',
        resolution_notes: orderRes.data.resolution_notes || '',
      });
      setShowOrderDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      OPEN: 'bg-warning/10 text-warning',
      ASSIGNED: 'bg-primary/10 text-primary',
      IN_PROGRESS: 'bg-chart-2/10 text-chart-2',
      ON_HOLD: 'bg-muted text-muted-foreground',
      COMPLETED: 'bg-success/10 text-success',
      VERIFIED: 'bg-success/10 text-success',
      CANCELLED: 'bg-destructive/10 text-destructive',
    };
    return <Badge className={styles[status] || 'bg-muted'}>{status.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      LOW: 'bg-muted text-muted-foreground',
      NORMAL: 'bg-primary/10 text-primary',
      HIGH: 'bg-warning/10 text-warning',
      URGENT: 'bg-destructive/10 text-destructive',
    };
    return <Badge className={styles[priority] || 'bg-muted'}>{priority}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="maintenance-page-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Maintenance</h1>
          <p className="text-muted-foreground">
            Work orders and preventive maintenance
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowCategoryDialog(true)} data-testid="new-category-btn">
            <Plus className="h-4 w-4 mr-2" />
            Category
          </Button>
          <Button onClick={() => setShowNewOrderDialog(true)} data-testid="new-order-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Work Order
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-open">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-warning/10">
                  <Clock className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Open</p>
                  <p className="text-2xl font-bold">{dashboard.open || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-in-progress">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Wrench className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold">{dashboard.in_progress || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-urgent">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-destructive/10">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Urgent</p>
                  <p className="text-2xl font-bold">{dashboard.urgent || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-completed">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-success/10">
                  <CheckCircle className="h-6 w-6 text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold">{dashboard.completed || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Table */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Work Orders</CardTitle>
            <CardDescription>All maintenance requests and work orders</CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="ASSIGNED">Assigned</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="ON_HOLD">On Hold</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter || 'all'} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40" data-testid="priority-filter">
                <SelectValue placeholder="All Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="NORMAL">Normal</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="URGENT">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Wrench className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No work orders found</p>
              <Button 
                variant="link" 
                onClick={() => setShowNewOrderDialog(true)}
                className="mt-2"
              >
                Create a work order
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => (
                  <TableRow 
                    key={order.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => viewOrderDetails(order)}
                    data-testid={`order-row-${order.id}`}
                  >
                    <TableCell className="font-medium">{order.order_number}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{order.title}</TableCell>
                    <TableCell>
                      {order.rooms?.room_number ? (
                        <span className="flex items-center gap-1">
                          <BedDouble className="h-4 w-4" />
                          {order.rooms.room_number}
                        </span>
                      ) : (
                        order.location_description || order.location_type
                      )}
                    </TableCell>
                    <TableCell>{getPriorityBadge(order.priority)}</TableCell>
                    <TableCell>{getStatusBadge(order.status)}</TableCell>
                    <TableCell>{order.issue_type}</TableCell>
                    <TableCell>{format(new Date(order.created_at), 'MMM d, HH:mm')}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            viewOrderDetails(order);
                          }}>
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {order.status === 'OPEN' && (
                            <DropdownMenuItem onClick={async (e) => {
                              e.stopPropagation();
                              await maintenanceApi.updateOrder(order.id, { status: 'IN_PROGRESS' });
                              loadData();
                              toast.success('Status updated');
                            }}>
                              Start Work
                            </DropdownMenuItem>
                          )}
                          {order.status === 'IN_PROGRESS' && (
                            <DropdownMenuItem onClick={async (e) => {
                              e.stopPropagation();
                              await maintenanceApi.updateOrder(order.id, { status: 'COMPLETED' });
                              loadData();
                              toast.success('Work order completed');
                            }}>
                              Mark Complete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Work Order Dialog */}
      <Dialog open={showNewOrderDialog} onOpenChange={setShowNewOrderDialog}>
        <DialogContent className="max-w-lg" data-testid="new-order-dialog">
          <DialogHeader>
            <DialogTitle>Create Work Order</DialogTitle>
            <DialogDescription>Submit a new maintenance request</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={orderForm.title}
                onChange={(e) => setOrderForm({ ...orderForm, title: e.target.value })}
                placeholder="Brief description of the issue"
                data-testid="order-title-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location Type</Label>
                <Select 
                  value={orderForm.location_type} 
                  onValueChange={(v) => setOrderForm({ ...orderForm, location_type: v })}
                >
                  <SelectTrigger data-testid="location-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ROOM">Room</SelectItem>
                    <SelectItem value="PUBLIC_AREA">Public Area</SelectItem>
                    <SelectItem value="BACK_OF_HOUSE">Back of House</SelectItem>
                    <SelectItem value="EXTERIOR">Exterior</SelectItem>
                    <SelectItem value="EQUIPMENT">Equipment</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {orderForm.location_type === 'ROOM' && (
                <div className="space-y-2">
                  <Label>Room</Label>
                  <Select 
                    value={orderForm.room_id} 
                    onValueChange={(v) => setOrderForm({ ...orderForm, room_id: v })}
                  >
                    <SelectTrigger data-testid="room-select">
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map(room => (
                        <SelectItem key={room.id} value={room.id}>
                          {room.room_number} - Floor {room.floor}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {orderForm.location_type !== 'ROOM' && (
              <div className="space-y-2">
                <Label>Location Description</Label>
                <Input
                  value={orderForm.location_description}
                  onChange={(e) => setOrderForm({ ...orderForm, location_description: e.target.value })}
                  placeholder="Describe the location"
                  data-testid="location-desc-input"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Type</Label>
                <Select 
                  value={orderForm.issue_type} 
                  onValueChange={(v) => setOrderForm({ ...orderForm, issue_type: v })}
                >
                  <SelectTrigger data-testid="issue-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REPAIR">Repair</SelectItem>
                    <SelectItem value="REPLACEMENT">Replacement</SelectItem>
                    <SelectItem value="INSPECTION">Inspection</SelectItem>
                    <SelectItem value="PREVENTIVE">Preventive</SelectItem>
                    <SelectItem value="EMERGENCY">Emergency</SelectItem>
                    <SelectItem value="UPGRADE">Upgrade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={orderForm.priority} 
                  onValueChange={(v) => setOrderForm({ ...orderForm, priority: v })}
                >
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {categories.length > 0 && (
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={orderForm.category_id} 
                  onValueChange={(v) => setOrderForm({ ...orderForm, category_id: v })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={orderForm.description}
                onChange={(e) => setOrderForm({ ...orderForm, description: e.target.value })}
                placeholder="Detailed description of the issue"
                rows={3}
                data-testid="order-description-input"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="room_out_of_order"
                checked={orderForm.room_out_of_order}
                onChange={(e) => setOrderForm({ ...orderForm, room_out_of_order: e.target.checked })}
                className="rounded border-input"
                data-testid="room-ooo-checkbox"
              />
              <Label htmlFor="room_out_of_order" className="text-sm">
                Mark room as Out of Order
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewOrderDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateOrder} 
              disabled={!orderForm.title}
              data-testid="create-order-btn"
            >
              Create Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work Order Detail Dialog */}
      <Dialog open={showOrderDetailDialog} onOpenChange={setShowOrderDetailDialog}>
        <DialogContent className="max-w-2xl" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Work Order #{selectedOrder?.order_number}</span>
              {selectedOrder && getStatusBadge(selectedOrder.status)}
            </DialogTitle>
            <DialogDescription>{selectedOrder?.title}</DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Location</p>
                  <p className="font-medium">
                    {selectedOrder.rooms?.room_number 
                      ? `Room ${selectedOrder.rooms.room_number}` 
                      : selectedOrder.location_description || selectedOrder.location_type
                    }
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Priority</p>
                  <p>{getPriorityBadge(selectedOrder.priority)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Type</p>
                  <p className="font-medium">{selectedOrder.issue_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">{format(new Date(selectedOrder.created_at), 'PPpp')}</p>
                </div>
              </div>
              
              {selectedOrder.description && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Description</p>
                  <p className="text-sm bg-muted p-3 rounded">{selectedOrder.description}</p>
                </div>
              )}
              
              {/* Update Form */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-medium">Update Work Order</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select 
                      value={updateForm.status} 
                      onValueChange={(v) => setUpdateForm({ ...updateForm, status: v })}
                    >
                      <SelectTrigger data-testid="update-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OPEN">Open</SelectItem>
                        <SelectItem value="ASSIGNED">Assigned</SelectItem>
                        <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                        <SelectItem value="ON_HOLD">On Hold</SelectItem>
                        <SelectItem value="COMPLETED">Completed</SelectItem>
                        <SelectItem value="VERIFIED">Verified</SelectItem>
                        <SelectItem value="CANCELLED">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Assign To</Label>
                    <Select 
                      value={updateForm.assigned_to} 
                      onValueChange={(v) => setUpdateForm({ ...updateForm, assigned_to: v })}
                    >
                      <SelectTrigger data-testid="assign-to-select">
                        <SelectValue placeholder="Select technician" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Resolution Notes</Label>
                  <Textarea
                    value={updateForm.resolution_notes}
                    onChange={(e) => setUpdateForm({ ...updateForm, resolution_notes: e.target.value })}
                    placeholder="Notes about the resolution"
                    rows={2}
                    data-testid="resolution-notes-input"
                  />
                </div>
              </div>
              
              {/* Activity Log */}
              {selectedOrder.logs?.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <h4 className="font-medium">Activity Log</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedOrder.logs.map((log, idx) => (
                      <div key={idx} className="text-sm flex gap-2 items-start">
                        <Badge variant="outline" className="shrink-0">{log.action}</Badge>
                        <span className="text-muted-foreground">{log.comment}</span>
                        <span className="text-xs text-muted-foreground ml-auto shrink-0">
                          {format(new Date(log.created_at), 'MMM d, HH:mm')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCommentDialog(true);
              }}
              data-testid="add-comment-btn"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Add Comment
            </Button>
            <Button variant="outline" onClick={() => setShowOrderDetailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateOrder} data-testid="update-order-btn">
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={showCommentDialog} onOpenChange={setShowCommentDialog}>
        <DialogContent data-testid="comment-dialog">
          <DialogHeader>
            <DialogTitle>Add Comment</DialogTitle>
            <DialogDescription>Add a note to this work order</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter your comment"
              rows={4}
              data-testid="comment-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddComment} disabled={!comment.trim()} data-testid="submit-comment-btn">
              Add Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>Create Category</DialogTitle>
            <DialogDescription>Add a new maintenance category</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                value={categoryForm.code}
                onChange={(e) => setCategoryForm({ ...categoryForm, code: e.target.value })}
                placeholder="e.g., PLUMB"
                data-testid="category-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Plumbing"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Category description"
                rows={2}
                data-testid="category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCategory}
              disabled={!categoryForm.code || !categoryForm.name}
              data-testid="create-category-btn"
            >
              Create Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
